import { BarChart3, Target } from 'lucide-react';
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  ProgressBar,
} from '@tutor/ui';
import { useMyProgress } from '../../hooks/queries';
import { formatDate, humaniseEnum } from '../../lib/format';

const MASTERY_TONE = {
  NOT_STARTED: 'neutral',
  EMERGING: 'danger',
  DEVELOPING: 'warning',
  SECURE: 'success',
  MASTERED: 'brand',
} as const;

const TREND_TONE = { IMPROVING: 'success', STEADY: 'neutral', DECLINING: 'danger' } as const;

export function ProgressPage() {
  const { data, isLoading, isError } = useMyProgress();

  if (isLoading) return <LoadingState />;
  if (isError || !data) return <ErrorState description="We could not load your progress." />;

  // Grouped by course so a student sees their subjects, not one long list of topics.
  const topicsByCourse = data.topics.reduce<Record<string, typeof data.topics>>((acc, topic) => {
    (acc[topic.courseName] ??= []).push(topic);
    return acc;
  }, {});

  return (
    <>
      <PageHeader title="Your progress" description="How you are doing across every subject." />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader title="Subjects" />
            <CardBody className="space-y-6">
              {data.subjects.length === 0 ? (
                <EmptyState
                  title="No progress recorded yet"
                  description="Progress builds up as you complete lessons and homework."
                  icon={<BarChart3 className="h-5 w-5" aria-hidden />}
                />
              ) : (
                data.subjects.map((subject) => (
                  <div key={subject.id}>
                    <div className="mb-2 flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-slate-900">{subject.subject.name}</h3>
                      <Badge tone={TREND_TONE[subject.trend]}>{humaniseEnum(subject.trend)}</Badge>
                    </div>

                    <ProgressBar value={subject.overallPercentage} showValue />

                    <dl className="mt-3 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
                      <div>
                        <dt className="text-slate-500">Target grade</dt>
                        <dd className="mt-0.5 font-medium text-slate-900">{subject.targetGrade ?? '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-slate-500">Predicted</dt>
                        <dd className="mt-0.5 font-medium text-slate-900">{subject.predictedGrade ?? '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-slate-500">Lessons done</dt>
                        <dd className="mt-0.5 font-medium text-slate-900">{subject.lessonsCompleted}</dd>
                      </div>
                      <div>
                        <dt className="text-slate-500">Homework done</dt>
                        <dd className="mt-0.5 font-medium text-slate-900">{subject.homeworkCompletionRate}%</dd>
                      </div>
                    </dl>
                  </div>
                ))
              )}
            </CardBody>
          </Card>

          {Object.entries(topicsByCourse).map(([course, topics]) => (
            <Card key={course}>
              <CardHeader title={course} description="Topic by topic" />
              <CardBody>
                <ul className="divide-y divide-slate-100">
                  {topics.map((topic) => (
                    <li key={topic.id} className="flex items-center gap-3 py-2.5">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-900">{topic.topicName}</p>
                        {topic.lastPracticedAt && (
                          <p className="text-xs text-slate-500">
                            Last practised {formatDate(topic.lastPracticedAt)}
                          </p>
                        )}
                      </div>
                      <div className="w-24 shrink-0">
                        <ProgressBar value={topic.confidence} size="sm" showValue={false} />
                      </div>
                      <Badge tone={MASTERY_TONE[topic.masteryLevel]}>{humaniseEnum(topic.masteryLevel)}</Badge>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ))}
        </div>

        <Card className="h-fit">
          <CardHeader title="Recent assessments" />
          <CardBody>
            {data.recentAssessments.length === 0 ? (
              <EmptyState title="No assessments yet" icon={<Target className="h-5 w-5" aria-hidden />} />
            ) : (
              <ul className="space-y-3">
                {data.recentAssessments.map((assessment) => (
                  <li key={assessment.id} className="rounded-lg border border-slate-100 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-slate-900">{assessment.title}</p>
                      {assessment.percentage !== null && (
                        <Badge tone={assessment.percentage >= 70 ? 'success' : 'warning'}>
                          {assessment.percentage}%
                        </Badge>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {assessment.subjectName ?? '—'} · {formatDate(assessment.assessedAt)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
}
